'use client';

import { KeyboardArrowDown, Menu as MenuIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Collapse,
  Drawer,
  IconButton,
  List,
  ListItem,
  Typography,
  useTheme,
} from '@mui/material';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Fragment, ReactNode, useMemo, useState } from 'react';

import { trackEvent } from '@/lib/analytics/gtm';
import { NestedMenu, NestedMenuItem } from '@/lib/queries/Menus';

import { MOBILE_MENU_TIMEOUT } from './MenuConstants';
import {
  closeAllMenus as closeAllMenusHelper,
  getDisplayText,
  getFontSize,
  getMenuItemUrl,
  hasSubmenu,
  isMenuItemActive,
  isMenuItemActiveParent,
} from './MenuHelpers';
import { createMenuStyles, getArrowRotation } from './MenuStyles';
import { MenuItemRendererProps } from './MenuTypes';

interface MobileMegaMenuProps {
  menu?: NestedMenu | undefined;
  talkToUs?: ReactNode;
  gettingStarted: ReactNode;
  variant?: 'dark' | 'light';
}

// Shared MenuItemRenderer component that works in both desktop dropdown and mobile drawer contexts

const MenuItemRenderer = ({
  items,
  currentPath,
  expandedItems,
  onToggleExpanded,
  onNavigate,
  getMenuItemUrl,
  getDisplayText,
  renderSubmenuItems,
  keyPrefix = 'mobile',
}: MenuItemRendererProps) => {
  const theme = useTheme();
  const styles = createMenuStyles(theme);

  return (
    <>
      {items.map((item, index) => {
        const itemHasSubmenu = hasSubmenu(item);
        const url = getMenuItemUrl(item);
        const text = getDisplayText(item);
        const key = `${keyPrefix}-${item.__typename}-${index}`;

        const isActive = isMenuItemActive(item, currentPath);
        const isActiveParent = isMenuItemActiveParent(item, currentPath);

        // Check if this item has both MenuUrl and MenuItems (combined menu item)
        const hasBothUrlAndItems = item.MenuUrl && item.MenuItems && item.MenuItems.length > 0;

        if (itemHasSubmenu) {
          const subMenuItems = item.MenuItems || item.SubMenu?.MenuItems || [];
          const submenuExpanded = expandedItems[key] || false;
          return (
            <Box key={key}>
              <ListItem sx={styles.menuItem.listItem}>
                <Button
                  fullWidth
                  onClick={() => onToggleExpanded(key)}
                  endIcon={<KeyboardArrowDown sx={getArrowRotation(submenuExpanded)} />}
                  className={`mega-menu-item mega-menu-item--submenu menu-item-level-1 ${isActiveParent ? 'mega-menu-item--active-parent' : ''}`}
                  sx={{
                    ...styles.menuItem.base,
                    ...styles.menuItem.button,
                    ...styles.menuItem.desktopSubmenu,
                    justifyContent: 'space-between',
                    textAlign: 'left',
                    px: 3,
                  }}
                >
                  <Typography variant="small">{text}</Typography>
                </Button>
              </ListItem>
              <Collapse in={submenuExpanded} timeout={MOBILE_MENU_TIMEOUT} unmountOnExit>
                <List component="div" disablePadding>
                  {/* If this item has both URL and MenuItems, add the page link first */}
                  {hasBothUrlAndItems && (
                    <ListItem sx={styles.menuItem.listItem}>
                      <Button
                        fullWidth
                        component={Link}
                        href={url}
                        onClick={() => {
                          trackEvent({
                            feature: 'navigation',
                            action: 'nav_header_click',
                            params: { link_text: text, link_href: url },
                          });
                          onNavigate();
                        }}
                        className={`mega-menu-item mega-menu-item--link menu-item-level-2 ${isActive ? 'mega-menu-item--active' : ''}`}
                        sx={{
                          ...styles.menuItem.base,
                          ...styles.menuItem.button,
                          ...styles.menuItem.desktopSubmenu,
                          px: 6, // Extra indentation for the parent link
                          ...(isActive && styles.menuItem.active),
                          fontWeight: 'bold', // Make the parent page link stand out
                        }}
                      >
                        <Typography variant="small">{text}</Typography>
                      </Button>
                    </ListItem>
                  )}
                  {renderSubmenuItems(subMenuItems, 1, key)}
                </List>
              </Collapse>
            </Box>
          );
        } else {
          return (
            <Box key={key}>
              <ListItem sx={styles.menuItem.listItem}>
                <Button
                  fullWidth
                  component={Link}
                  href={url}
                  onClick={() => {
                    trackEvent({
                      feature: 'navigation',
                      action: 'nav_header_click',
                      params: { link_text: text, link_href: url },
                    });
                    onNavigate();
                  }}
                  className={`mega-menu-item mega-menu-item--link menu-item-level-1 ${isActive ? 'mega-menu-item--active' : ''}`}
                  sx={{
                    ...styles.menuItem.base,
                    ...styles.menuItem.button,
                    ...styles.menuItem.desktopSubmenu,
                    px: 3,
                    ...(isActive && styles.menuItem.active),
                  }}
                >
                  <Typography variant="small">{text}</Typography>
                </Button>
              </ListItem>
            </Box>
          );
        }
      })}
    </>
  );
};

export const MobileMegaMenu = ({
  menu = undefined,
  talkToUs,
  gettingStarted,
  variant = 'light',
}: MobileMegaMenuProps) => {
  const theme = useTheme();
  const styles = createMenuStyles(theme);
  const currentPath = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpandedItems, setMobileExpandedItems] = useState<{
    [key: string]: boolean;
  }>({});
  const isDarkTheme = variant === 'dark';

  const mainMenuItems = useMemo(
    () =>
      (menu?.MenuItems || []).filter((item) => {
        // Exclude items with null SubMenu and null SubMenuCustomLabel
        if (item.__typename === 'ComponentMenuMenuSubMenu') {
          return !(item.SubMenu === null && item.SubMenuCustomLabel === null);
        }
        return true; // Keep all other menu item types
      }),
    [menu]
  );

  // Mobile menu handlers
  const handleMobileToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMobileItemToggle = (itemKey: string) => {
    setMobileExpandedItems((prev) => {
      const isCurrentlyExpanded = prev[itemKey];

      if (isCurrentlyExpanded) {
        // If closing, just close this item and all its children
        const newState = { ...prev };
        Object.keys(newState).forEach((key) => {
          if (key === itemKey || key.startsWith(`${itemKey}-`)) {
            newState[key] = false;
          }
        });
        return newState;
      } else {
        // If opening, close all siblings at the same level
        const newState = { ...prev };

        // Parse the key to find the level (count the number of typename-depth-index patterns)
        const keyParts = itemKey.split('-');
        let parentKey = '';

        // Find the parent key by removing the last typename-depth-index pattern
        for (let i = 0; i < keyParts.length - 3; i++) {
          if (i > 0) parentKey += '-';
          parentKey += keyParts[i];
        }

        // Close all siblings (items with the same parent but different last part)
        Object.keys(newState).forEach((key) => {
          if (
            key !== itemKey &&
            key.startsWith(parentKey) &&
            key.split('-').length === keyParts.length
          ) {
            // This is a sibling at the same level
            newState[key] = false;
            // Also close all children of this sibling
            Object.keys(newState).forEach((childKey) => {
              if (childKey.startsWith(`${key}-`)) {
                newState[childKey] = false;
              }
            });
          }
        });

        // Open the target item
        newState[itemKey] = true;
        return newState;
      }
    });
  };

  // Function to close all menus when navigating
  const closeAllMenus = () => {
    closeAllMenusHelper(setMobileExpandedItems, setMobileOpen);
  };

  // Helper function for rendering submenu items (mobile only) with deep nesting support
  const renderSubmenuItems = (
    items: NestedMenuItem[],
    depth: number = 0,
    parentKey: string = ''
  ): ReactNode[] => {
    const styles = createMenuStyles(theme);

    return items.map((subItem, index) => {
      const text = getDisplayText(subItem);
      const key = `${parentKey}-${subItem.__typename}-${depth}-${index}`;
      const itemHasSubmenu = hasSubmenu(subItem);
      const url = getMenuItemUrl(subItem);
      const isActive = isMenuItemActive(subItem, currentPath);
      const isActiveParent = isMenuItemActiveParent(subItem, currentPath);

      // Check if this item has both MenuUrl and MenuItems (combined menu item)
      const hasBothUrlAndItems =
        subItem.MenuUrl && subItem.MenuItems && subItem.MenuItems.length > 0;

      if (itemHasSubmenu) {
        const subMenuItems = subItem.MenuItems || subItem.SubMenu?.MenuItems || [];

        // For mobile, all levels use the same collapsing behavior (no side-popping)
        const isExpanded = mobileExpandedItems[key];
        return (
          <Fragment key={key}>
            <ListItem sx={styles.menuItem.listItem}>
              <Button
                fullWidth
                onClick={() => handleMobileItemToggle(key)}
                endIcon={
                  <KeyboardArrowDown
                    sx={{
                      ...getArrowRotation(isExpanded),
                      fontSize: getFontSize(depth),
                    }}
                  />
                }
                className={`mega-menu-item mega-menu-item--submenu menu-item-level-${depth + 1} ${isActiveParent ? 'mega-menu-item--active-parent' : ''}`}
                sx={{
                  ...styles.menuItem.base,
                  ...styles.menuItem.button,
                  py: 1.5,
                  minHeight: 40,
                  justifyContent: 'space-between',
                  textAlign: 'left',
                  pl: depth === 0 ? 3 : 3 + depth * 1,
                  pr: 3,
                }}
              >
                <Typography variant="small">{text}</Typography>
              </Button>
            </ListItem>
            <Collapse in={isExpanded} timeout={MOBILE_MENU_TIMEOUT} unmountOnExit>
              <List component="div" disablePadding>
                {/* If this item has both URL and MenuItems, add the page link first */}
                {hasBothUrlAndItems && (
                  <ListItem sx={styles.menuItem.listItem}>
                    <Button
                      fullWidth
                      component={Link}
                      href={url}
                      onClick={() => {
                        trackEvent({
                          feature: 'navigation',
                          action: 'nav_header_click',
                          params: { link_text: text, link_href: url },
                        });
                        closeAllMenus();
                      }}
                      className={`mega-menu-item mega-menu-item--link menu-item-level-${depth + 2} ${isActive ? 'mega-menu-item--active' : ''}`}
                      sx={{
                        ...styles.menuItem.base,
                        ...styles.menuItem.button,
                        py: 1.5,
                        minHeight: 40,
                        pl: depth === 0 ? 6 : 6 + depth * 1, // Extra indentation for the parent link
                        pr: 3,
                        ...(isActive && styles.menuItem.activeSubmenu),
                        fontWeight: 'bold', // Make the parent page link stand out
                      }}
                    >
                      <Typography variant="small">{text}</Typography>
                    </Button>
                  </ListItem>
                )}
                {renderSubmenuItems(subMenuItems, depth + 1, key)}
              </List>
            </Collapse>
          </Fragment>
        );
      } else {
        // Regular menu item
        return (
          <ListItem key={key} sx={styles.menuItem.listItem}>
            <Button
              fullWidth
              component={Link}
              href={url}
              onClick={() => {
                trackEvent({
                  feature: 'navigation',
                  action: 'nav_header_click',
                  params: { link_text: text, link_href: url },
                });
                closeAllMenus();
              }}
              className={`mega-menu-item mega-menu-item--link menu-item-level-${depth + 1} ${isActive ? 'mega-menu-item--active' : ''}`}
              sx={{
                ...styles.menuItem.base,
                ...styles.menuItem.button,
                py: 1.5,
                minHeight: 40,
                pl: depth === 0 ? 3 : 3 + depth * 1,
                pr: 3,
                ...(isActive && styles.menuItem.activeSubmenu),
              }}
            >
              <Typography variant="small">{text}</Typography>
            </Button>
          </ListItem>
        );
      }
    });
  };

  return (
    <Box data-component="MobileMegaMenu">
      <Box sx={{ display: { xs: 'flex', lg: 'none' } }}>
        <IconButton
          size="large"
          aria-label="open navigation menu"
          onClick={handleMobileToggle}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: isDarkTheme ? theme.palette.common.white : theme.palette.common.black,
            backgroundColor: isDarkTheme ? theme.palette.purple[800] : theme.palette.common.white,
            borderRadius: '50%',
            py: 0,
            width: { xs: 44, sm: 48 },
            height: { xs: 44, sm: 48 },
            '&:hover,&:focus': {
              backgroundColor: theme.palette.brand.primary,
              background: 'none',
            },
          }}
        >
          <MenuIcon
            sx={{
              fontSize: { xs: '2rem', sm: '2.5rem' },
              color: isDarkTheme ? theme.palette.common.white : theme.palette.common.black,
            }}
          />
        </IconButton>
      </Box>

      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleMobileToggle}
        sx={{
          display: { xs: 'block', lg: 'none' },
          '& .MuiDrawer-paper': {
            ...styles.mobileDrawer.paper,
            paddingTop: 2,
            paddingBottom: 0,
            paddingInline: 1,
          },
        }}
      >
        <List>
          <MenuItemRenderer
            items={[...mainMenuItems]}
            currentPath={currentPath}
            expandedItems={mobileExpandedItems}
            onToggleExpanded={handleMobileItemToggle}
            onNavigate={closeAllMenus}
            getMenuItemUrl={getMenuItemUrl}
            getDisplayText={getDisplayText}
            renderSubmenuItems={renderSubmenuItems}
            keyPrefix="mobile"
          />
        </List>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            height: '100%',
            gap: 2,
            px: 6,
            pt: 2,
            pb: 4,
          }}
        >
          {talkToUs}
          {gettingStarted}
        </Box>
      </Drawer>
    </Box>
  );
};
